"use client";

import React, { useEffect, useRef, useState, useMemo } from "react";
import { MessageCircle, Tags, Headset } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>(options?: UseRevealOnScrollOptions) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Memoize options to prevent unnecessary dependency changes
  const memoizedOptions = useMemo(() => {
    return {
      once: options?.once ?? true,
      threshold: options?.threshold ?? 0.2,
      rootMargin: options?.rootMargin ?? "0px"
    };
  }, [options?.once, options?.threshold, options?.rootMargin]);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (memoizedOptions.once) {
          observer.unobserve(entry.target);
        }
      } else if (!memoizedOptions.once) {
        setIsVisible(false);
      }
    }, {
      threshold: memoizedOptions.threshold,
      rootMargin: memoizedOptions.rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [memoizedOptions]);
  return {
    ref,
    isVisible
  };
}
const features = [{
  title: "Vibe Coding for WordPress",
  description: "Build and refine your site by describing what you want. Webshingle turns your prompts into WordPress pages, sections, copy, and site structure so you can move faster without writing code.",
  icon: MessageCircle
}, {
  title: "AI Content Help",
  description: "Never stare at a blank page again. Our integrated AI helps generate high-converting copy and stunning images tailored to your brand.",
  icon: Tags
}, {
  title: "Managed Hosting",
  description: "Enjoy lightning-fast speeds and 99.9% uptime. We handle the technical heavy lifting so your site stays secure and always online.",
  icon: Headset
}];
export default function WhyChooseUs() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="223817" id="the-webshingle-advantage" className="bg-background py-24 px-4 sm:px-6 lg:px-8">
      <div ref={sectionRef} className={`mx-auto max-w-7xl transition-all duration-1000 transform ${isSectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-extrabold font-default text-secondary mb-4 tracking-tight">
            The Webshingle Advantage
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto font-default">
            Everything you need to launch a high-converting WordPress site from a single prompt.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, i) => {
          const Icon = feature.icon;
          return <div data-index={i} key={i} className="h-full">
                <Card className="h-full bg-card border-border/60 shadow-md hover:-translate-y-2 hover:shadow-xl transition-all duration-300 group rounded-2xl">
                  <CardHeader className="pb-4 pt-10 px-10">
                    <div className="relative inline-flex mb-6 transition-transform duration-300 group-hover:scale-110">
                      <div className="p-4 bg-primary rounded-lg text-secondary">
                        <Icon className="w-8 h-8" strokeWidth={2} />
                      </div>
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-secondary rounded-lg border-2 border-card shadow-sm"></div>
                    </div>
                    <CardTitle className="text-2xl font-bold font-default text-secondary">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-10 pb-10">
                    <p className="text-muted-foreground leading-relaxed text-lg font-default">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}